/* ── Drone Motion Trails ─────────────────────────────────────────
 * Subtle motion-trail particles behind fast-moving drones. Trails
 * fade with age and only emit when speed exceeds a threshold; their
 * opacity scales linearly with drone speed up to a saturation point.
 *
 * Uses a single pooled `THREE.Points` with a per-vertex age attribute
 * — no allocations per frame, and the whole pool participates in one
 * draw call. Lifetimes are tracked CPU-side and dead particles are
 * recycled in-place.
 *
 * Public API matches the project's renderer convention:
 *   - constructor adds to scene
 *   - update(dt, dronePositions, droneVelocities) animates
 *   - dispose() cleans up
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, attribute, float, uniform, vec3, vec4, smoothstep } from 'three/tsl';
import { setEmissiveNode } from './tslUtils';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';

/** Speed (units/sec) at which a drone starts emitting trail particles. */
const TRAIL_SPEED_THRESHOLD = 4.0;
/** Speed at which trail opacity saturates at full strength. */
const TRAIL_SPEED_SATURATION = 18.0;
/** Particle lifetime in seconds. */
const PARTICLE_LIFE = 0.6;
/** Per-drone emit interval — caps spawn rate so a fast drone produces a ribbon, not a flood. */
const EMIT_INTERVAL = 0.04;
/** Cap on how many particles a single drone can keep alive at once. */
const PER_DRONE_PARTICLE_BUDGET = 12;

interface DroneEmitState {
  /** Seconds until next eligible emit for this drone. */
  cooldown: number;
}

/**
 * Builds the TSL color/size graph for a single trail particle.
 *
 * Inputs come through per-vertex attributes (`aAge`, `aMaxLife`,
 * `aIntensity`) so all live particles render in one draw call.
 */
function buildTrailNodes(uColor: ReturnType<typeof uniform>) {
  const aAge = attribute('aAge', 'float');
  const aMaxLife = attribute('aMaxLife', 'float');
  const aIntensity = attribute('aIntensity', 'float');

  // Linear life ratio; clamped so dead-but-not-yet-recycled particles read as 0.
  const t = float(aAge).div(float(aMaxLife).max(0.0001)).clamp(0.0, 1.0);

  const sizeNode = Fn(() => {
    // Particles start big and shrink; the multiplier converts world units to
    // WebGPU 1px points scale, matching EffectsManager explosion sizing.
    const grow = float(1.0).sub(t.mul(0.55));
    return float(20.0).mul(grow).mul(float(aIntensity).max(0.05));
  })();

  const colorNode = Fn(() => {
    const fade = float(1.0).sub(smoothstep(float(0.0), float(1.0), t));
    const alpha = fade.mul(float(aIntensity)).mul(0.85);
    return vec4(vec3(uColor), alpha);
  })();

  return { sizeNode, colorNode };
}

export class DroneTrails {
  private readonly scene: THREE.Scene;
  private readonly points: THREE.Points;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsNodeMaterial;
  private readonly positions: Float32Array;
  private readonly ages: Float32Array;
  private readonly maxLives: Float32Array;
  private readonly intensities: Float32Array;
  private readonly maxParticles: number;
  /** Cursor for round-robin slot assignment. */
  private cursor = 0;
  private readonly emitStates = new Map<string, DroneEmitState>();
  private readonly _scratch = new THREE.Vector3();

  constructor(
    scene: THREE.Scene,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
  ) {
    this.scene = scene;
    // Budget scales with the project's existing trail budget so quality
    // presets affect motion-trail density too. The factor (×0.6) keeps
    // motion trails subtler than the engine cone trails.
    this.maxParticles = Math.max(48, Math.round(qualityPreset.trailBudget * 0.6 * 6));

    this.positions = new Float32Array(this.maxParticles * 3);
    this.ages = new Float32Array(this.maxParticles);
    this.maxLives = new Float32Array(this.maxParticles);
    this.intensities = new Float32Array(this.maxParticles);

    // Initialize all particles as expired so the first frame doesn't render
    // garbage data.
    for (let i = 0; i < this.maxParticles; i++) {
      this.ages[i] = PARTICLE_LIFE + 1;
      this.maxLives[i] = PARTICLE_LIFE;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('aAge', new THREE.BufferAttribute(this.ages, 1));
    this.geometry.setAttribute('aMaxLife', new THREE.BufferAttribute(this.maxLives, 1));
    this.geometry.setAttribute('aIntensity', new THREE.BufferAttribute(this.intensities, 1));

    const uColor = uniform(new THREE.Color(0x9bdcff));

    this.material = new THREE.PointsNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });

    const { sizeNode, colorNode } = buildTrailNodes(uColor);
    this.material.sizeNode = sizeNode;
    this.material.colorNode = colorNode;
    setEmissiveNode(this.material, colorNode);

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
    this.scene.add(this.points);
  }

  /**
   * Per-frame tick. Advances particle ages and emits new particles for any
   * drone moving above {@link TRAIL_SPEED_THRESHOLD}. The map is keyed by
   * drone id and each entry contributes both position and velocity.
   */
  update(
    deltaMs: number,
    droneMotion: Map<string, { position: THREE.Vector3; velocity: THREE.Vector3 }>,
  ): void {
    const dt = Math.max(deltaMs / 1000, 1 / 240);
    let emitted = false;

    for (let i = 0; i < this.maxParticles; i++) {
      if (this.ages[i] < this.maxLives[i]) {
        this.ages[i] += dt;
      }
    }

    droneMotion.forEach((m, id) => {
      const speed = m.velocity.length();

      let state = this.emitStates.get(id);
      if (!state) {
        state = { cooldown: 0 };
        this.emitStates.set(id, state);
      }
      state.cooldown -= dt;

      if (speed < TRAIL_SPEED_THRESHOLD) return;
      if (state.cooldown > 0) return;
      if (this.countLiveParticlesNear(m.position) >= PER_DRONE_PARTICLE_BUDGET) return;

      state.cooldown = EMIT_INTERVAL;

      const intensity = THREE.MathUtils.clamp(
        (speed - TRAIL_SPEED_THRESHOLD) /
          (TRAIL_SPEED_SATURATION - TRAIL_SPEED_THRESHOLD),
        0.05,
        1.0,
      );

      // Spawn slightly behind the drone, opposite its motion vector.
      const offsetScale = -1.4 / speed;
      this._scratch.set(
        m.position.x + m.velocity.x * offsetScale,
        m.position.y + m.velocity.y * offsetScale,
        m.position.z + m.velocity.z * offsetScale,
      );

      this.spawnParticle(this._scratch, intensity);
      emitted = true;
    });

    // Drop emit-state for drones we no longer see; their particles age out.
    this.emitStates.forEach((_state, id) => {
      if (!droneMotion.has(id)) this.emitStates.delete(id);
    });

    // aAge advances every frame; position/intensity only when emitting.
    (this.geometry.attributes.aAge as THREE.BufferAttribute).needsUpdate = true;
    if (emitted) {
      (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      (this.geometry.attributes.aIntensity as THREE.BufferAttribute).needsUpdate = true;
    }
  }

  dispose(): void {
    this.scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
    this.emitStates.clear();
  }

  private spawnParticle(position: THREE.Vector3, intensity: number): void {
    // Round-robin: cursor advances every emit, so the oldest slot is reused.
    const slot = this.cursor % this.maxParticles;
    this.cursor += 1;

    const off = slot * 3;
    this.positions[off] = position.x;
    this.positions[off + 1] = position.y;
    this.positions[off + 2] = position.z;
    this.ages[slot] = 0;
    this.maxLives[slot] = PARTICLE_LIFE;
    this.intensities[slot] = intensity;
  }

  /**
   * Approximate per-drone particle count via spatial proximity. Avoids
   * tracking owner ids per particle; only fresh (<half-life) particles count.
   */
  private countLiveParticlesNear(position: THREE.Vector3): number {
    let count = 0;
    const r2 = 9;
    const ageCutoff = PARTICLE_LIFE * 0.5;
    for (let i = 0; i < this.maxParticles; i++) {
      const age = this.ages[i];
      if (age >= this.maxLives[i] || age > ageCutoff) continue;
      const off = i * 3;
      const dx = this.positions[off] - position.x;
      const dy = this.positions[off + 1] - position.y;
      const dz = this.positions[off + 2] - position.z;
      if (dx * dx + dy * dy + dz * dz < r2) count++;
    }
    return count;
  }
}
